import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import useAuthGuard from '../hooks/useAuthGuard';
import Layout from '../components/Layout';
import {
  pocApi, frameImageURL, TRACK_VERDICTS,
  FALSE_REASONS, DUPLICATE_REASONS, MERGED_REASONS, BOX_ISSUES,
} from '../utils/pocApi';

const REASONS_BY_VERDICT = {
  machine: BOX_ISSUES,
  false: FALSE_REASONS,
  duplicate: DUPLICATE_REASONS,
  merged: MERGED_REASONS,
};


// PH2 の確認画面。
//
// 軌跡の本数が実測の台数と合っていても、中身が正しいとは限らない。一度
// 76本が実測77台とほぼ一致したことがあり、目視したら12本がガチャ、7本が人、
// 4本が両替機だった。数だけを見て判断しないために、この画面がある。
//
// 判定は3択。「台」と数えたものだけが台数になる。
export default function PocTracks() {
  useAuthGuard();
  const { datasetId } = useParams();

  const [data, setData] = useState(null);
  const [source, setSource] = useState('');
  const [filter, setFilter] = useState('pending');
  // **いま判定したカードは、未判定で絞っていても消さない。**
  //
  // 理由は判定の後に出るのに、判定を押した瞬間にカードが「未判定」の絞り込みから
  // 外れて視界から消えていた。**理由ボタンに手が届かない**ので、誰も理由を
  // 入力できない（実際に入力できないと報告があった）。
  //
  // 消さないのはこの画面を開いている間だけ。絞り込みを切り替えるか読み直せば
  // 消える——「未判定」が終わらない一覧になっても困る。
  const [justDecided, setJustDecided] = useState(() => new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async (src) => {
    setLoading(true);
    try {
      const res = await pocApi.tracks(datasetId, src ? { source: src } : {});
      setData(res);
      setSource(res.source || '');
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [datasetId]);

  useEffect(() => { load(''); }, [load]);

  const save = async (track, verdict, note) => {
    try {
      await pocApi.setTrackVerdict(track.id, { verdict, note: note || '' });
      setData((prev) => ({
        ...prev,
        tracks: prev.tracks.map((t) => (
          t.id === track.id ? { ...t, verdict: verdict || null, verdictNote: note || null } : t
        )),
      }));
    } catch (e) {
      setError(e.message);
    }
  };

  const decide = async (track, verdict) => {
    // Pressing the same verdict again clears it: a mis-click must be
    // undoable without hunting for a separate control.
    const next = track.verdict === verdict ? '' : verdict;
    setJustDecided((prev) => new Set(prev).add(track.id));
    // 判定を変えたら理由は必ず消す。「誤検出・ガチャ」を「台」に直したときに
    // 「ガチャ」が残ると、台でないものの理由が集計に混ざる。
    await save(track, next, '');
  };

  // 理由は判定の後に付く。押し直しで解除できるのは判定と同じ。
  const setReason = async (track, reason) => {
    if (!track.verdict) return;
    await save(track, track.verdict, track.verdictNote === reason ? '' : reason);
  };

  const summary = useMemo(() => {
    const t = data?.tracks || [];
    const by = (v) => t.filter((x) => x.verdict === v).length;
    return {
      total: t.length,
      machine: by('machine'),
      false: by('false'),
      duplicate: by('duplicate'),
      merged: by('merged'),
      pending: t.filter((x) => !x.verdict).length,
    };
  }, [data]);

  // **理由の内訳。判定した先から見えないと、理由を押す意味が伝わらない。**
  // 「誤検出32本」では次に何を直すか決まらないが、「うちガチャ20・人7」なら
  // 学習データにガチャを足す話になる。多い順。
  const reasonCounts = useMemo(() => {
    const n = {};
    for (const t of data?.tracks || []) {
      if (!t.verdict || !t.verdictNote) continue;
      // **判定ごとの辞書から引く。** 全部を1つに混ぜて最初の一致を取ると、
      // 誤結合の `sparse`（コマが疎で次の台に乗り移った）が重複の `sparse`
      // （コマが疎で追えない）として数えられる。同じキーが別の判定で
      // 別の意味を持つのは自然なので、キーを分けるのではなく引き方を直す。
      const label = (REASONS_BY_VERDICT[t.verdict] || [])
        .find((r) => r.key === t.verdictNote)?.label || t.verdictNote;
      const verdictLabel = TRACK_VERDICTS.find((v) => v.key === t.verdict)?.label || t.verdict;
      const key = `${verdictLabel}: ${label}`;
      n[key] = (n[key] || 0) + 1;
    }
    return Object.entries(n).sort((a, b) => b[1] - a[1]);
  }, [data]);

  const shown = useMemo(() => {
    const t = data?.tracks || [];
    if (filter === 'all') return t;
    if (filter === 'pending') return t.filter((x) => !x.verdict || justDecided.has(x.id));
    return t.filter((x) => x.verdict === filter);
  }, [data, filter, justDecided]);

  return (
    <Layout>
      <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
        <div>
          <h5 className="mb-1">PH2 — 軌跡の確認</h5>
          <div className="text-muted" style={{ fontSize: '0.8rem' }}>
            動画全体で同じ台を束ねた結果です。「台」と数えたものだけが台数になります。
          </div>
        </div>
        <div className="d-flex gap-2">
          <Link className="btn btn-sm btn-outline-secondary" to={`/poc/${datasetId}/report`}>レポート</Link>
          <Link className="btn btn-sm btn-outline-secondary" to="/poc">一覧</Link>
        </div>
      </div>

      {error && <div className="alert alert-danger py-2">{error}</div>}

      <div className="card mb-3">
        <div className="card-body py-3">
          <div className="row g-2 align-items-end">
            <div className="col-md-4">
              <label className="form-label small mb-1">検出の設定</label>
              <select
                className="form-select form-select-sm"
                value={source}
                onChange={(e) => { setSource(e.target.value); load(e.target.value); }}
              >
                {(data?.sources || []).map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="col-md-8">
              <div className="d-flex flex-wrap gap-3 justify-content-md-end">
                <Stat label="軌跡" value={summary.total} />
                <Stat label="台" value={summary.machine} tone="success" />
                <Stat label="誤検出" value={summary.false} tone="danger" />
                <Stat label="重複" value={summary.duplicate} tone="warning" />
                <Stat label="誤結合" value={summary.merged} tone="warning" />
                <Stat label="未判定" value={summary.pending} />
              </div>
            </div>
          </div>

          {reasonCounts.length > 0 && (
            <div className="mt-3 pt-2 border-top">
              <div className="text-muted" style={{ fontSize: '0.7rem' }}>理由の内訳</div>
              <div className="d-flex flex-wrap gap-2 mt-1">
                {reasonCounts.map(([label, n]) => (
                  <span key={label} className="badge bg-light text-dark border">
                    {label} {n}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="btn-group btn-group-sm mt-3">
            {[['pending', '未判定'], ['all', 'すべて'], ['machine', '台'],
              ['false', '誤検出'], ['duplicate', '重複'], ['merged', '誤結合']]
              .map(([k, l]) => (
                <button
                  key={k}
                  className={`btn ${filter === k ? 'btn-dark' : 'btn-outline-dark'}`}
                  onClick={() => { setFilter(k); setJustDecided(new Set()); }}
                >
                  {l}
                </button>
              ))}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-muted">読み込み中…</div>
      ) : shown.length === 0 ? (
        <div className="text-muted">この条件の軌跡はありません。</div>
      ) : (
        <div className="row g-3">
          {shown.map((t) => (
            <TrackCard key={t.id} track={t} onDecide={decide} onReason={setReason} />
          ))}
        </div>
      )}
    </Layout>
  );
}

function Stat({ label, value, tone }) {
  return (
    <div className="text-center">
      <div className="text-muted" style={{ fontSize: '0.7rem' }}>{label}</div>
      <div className={`fw-bold${tone ? ` text-${tone}` : ''}`} style={{ fontSize: '1.15rem' }}>{value}</div>
    </div>
  );
}

function TrackCard({ track, onDecide, onReason }) {
  const reasons = REASONS_BY_VERDICT[track.verdict] || [];
  const samples = Array.isArray(track.samples) ? track.samples : [];
  return (
    <div className="col-12 col-lg-6">
      <div className="card h-100">
        <div className="card-body py-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <div className="fw-semibold">#{track.seq}</div>
            <div className="text-muted" style={{ fontSize: '0.72rem' }}>
              {track.frameCount} フレーム ・ {Number(track.firstSec).toFixed(0)}〜{Number(track.lastSec).toFixed(0)}s
              ・ 最大 {Number(track.maxScore).toFixed(2)}
            </div>
          </div>

          <div className="d-flex gap-2 mb-2" style={{ overflowX: 'auto' }}>
            {samples.map((s, i) => <Sample key={i} sample={s} />)}
            {samples.length === 0 && (
              <div className="text-muted small">代表フレームがありません</div>
            )}
          </div>

          <div className="btn-group btn-group-sm w-100">
            {TRACK_VERDICTS.map((v) => (
              <button
                key={v.key}
                className={`btn ${track.verdict === v.key ? `btn-${v.variant}` : `btn-outline-${v.variant}`}`}
                onClick={() => onDecide(track, v.key)}
              >
                {v.label}
              </button>
            ))}
          </div>

          {/* 理由。「台」にも出すが、そちらは**台数を変えない枠の問題**で、
              意味が違うので見出しで分ける。どれも任意。 */}
          {reasons.length > 0 && (
            <div className="mt-2">
              <div className="text-muted small mb-1">
                {track.verdict === 'machine'
                  ? '枠の問題（任意・台数は変わりません）'
                  : '理由（任意・押し直しで解除）'}
              </div>
              <div className="d-flex flex-wrap gap-1">
                {reasons.map((r) => (
                  <button
                    key={r.key}
                    className={`btn btn-sm ${
                      track.verdictNote === r.key ? 'btn-secondary' : 'btn-outline-secondary'
                    }`}
                    onClick={() => onReason(track, r.key)}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// The box is drawn over the frame rather than baked into the image, so one
// stored frame can serve several tracks that appear in it.
function Sample({ sample }) {
  const box = sample.box || {};
  return (
    <div className="position-relative flex-shrink-0" style={{ width: 150 }}>
      <img
        src={frameImageURL(sample.url)}
        alt=""
        style={{ width: '100%', display: 'block', borderRadius: 4, background: '#111' }}
      />
      {box.w > 0 && (
        <div
          style={{
            position: 'absolute',
            left: `${box.x * 100}%`, top: `${box.y * 100}%`,
            width: `${box.w * 100}%`, height: `${box.h * 100}%`,
            border: '2px solid #22c55e', pointerEvents: 'none',
          }}
        />
      )}
      <div
        className="position-absolute text-white px-1"
        style={{ left: 0, bottom: 0, fontSize: '0.62rem', background: 'rgba(0,0,0,.6)' }}
      >
        {Number(sample.sec).toFixed(1)}s
      </div>
    </div>
  );
}
